import { Injectable } from "@angular/core";
import { Subscription, Subject } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { auditTime } from "rxjs/operators";
// BFX
// models
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  BfxAutocompleteFavoriteTypeEnum,
  BfxAutocompleteFavoriteItemFormatEnum,
} from "../models/bfx-interface-controls.enums";
import {
  BfxAutocompleteClientModel,
  BfxAutocompleteAccountModel,
  BfxAutocompleteGenericModel,
} from "../models/bfx-interface-controls.models";
// services
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
// cubiq-services
import {
  ProfilePromiseActionService,
  ClientPromiseLookupService,
  AccountPromiseLookupService,
  CcyPairPromiseLookupService,
  CcyPairLookupService,
  StaticDataPrivatizationType,
} from "shared/cubiq-services";
import { ProfileSettingKeys } from "shared/cubiq-services";
import { ClientAccountVisibilityType } from "shared/cubiq-services";
import { BfxUserProfileService } from "../../bfx-common/services/bfx-user-profile.service";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class BfxAutocompleteFavoritesProviderService {
  /**********************************************************************/
  /* DATA */
  private config: any = {
    debug: false,
    favoritesListSize: {
      clients: 3,
      accounts: 3,
      ccyPairs: 3,
    },
  };

  /* these variables (control) are used in order to avoid multiple concurrent model loads(lookups), by facilitating lock mechanism  */
  private control: any = {
    modelsLoading: false,
    modelsLoadingPromiseCount: 0,
  };

  private data: any = {
    raw: {
      clients: new Array<string>(),
      accounts: new Array<string>(),
      ccyPairs: new Array<string>(),
    },
    display: {
      clients: new Array<BfxAutocompleteClientModel>(),
      accounts: new Array<BfxAutocompleteAccountModel>(),
      ccyPairs: new Array<BfxAutocompleteGenericModel>(),
    },
    models: {
      // for Impls  not in use
      clients: new Array<CubiqTS.ClientImpl>(),
      accounts: new Array<CubiqTS.AccountImpl>(),
      ccyPairs: new Array<BfxAutocompleteGenericModel>(), // added just for consistency
    },
  };

  /**********************************************************************/
  /* SUBSCRIPTIONs */
  private configurationSubscription: Subscription;
  private saveSubject = new Subject<void>();
  private saveSubjectAuditInit = false;

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    private _configService: BfxConfigService,
    protected _translationService: TranslateService,
    protected _profileService: ProfilePromiseActionService,
    private _clientLookupService: ClientPromiseLookupService,
    private _accountLookupService: AccountPromiseLookupService,
    private _ccyPairLookupService: CcyPairPromiseLookupService,
    private userProfileService: BfxUserProfileService,
  ) {
    this.initialize();
  }
  /**********************************************************************/
  /* INITIALIZATION */
  private subscribeToConfiguration(): void {
    this.configurationSubscription = this._configService.uiConfig$.subscribe((config) => {
      this.config.debug = config.general.debug;
    });
  }

  private initialize(): void {
    this.subscribeToConfiguration();
    this.loadFavorites();

    if (this.saveSubjectAuditInit === false) {
      this.saveSubjectAuditInit = true;
      let at = 90000 + (Math.floor(Math.random() * Math.floor(60000)) - 30000);

      logger.out.info(`User profile favorites saving will be delayed upto ${at}ms`);
      this.saveSubject
        .pipe(
          // 90 +/- 30
          auditTime(at),
        )
        .subscribe(() => {
          // request save on server
          let profileConfigKey: string = ProfileSettingKeys.userProfileFavoritesAutocomplete;

          let profileSettings: Array<CubiqTS.ProfileSettingImpl> = [];
          profileSettings.push(
            this.userProfileService.createProfileSettingObject(
              (profileConfigKey = profileConfigKey.toLowerCase()),
              JSON.stringify(this.data.raw),
            ),
          );
          this._profileService.updateProfile(profileSettings, false).then(() => {
            logger.out.info("User profile favorites updated.");
            // logger.out.info('User profile favorites updated = ', this.data.raw);
          });
        });
    }
  }

  /**********************************************************************/
  /* Profile TEst */

  private resetFavorites(): void {
    let clientsSample = []; // 'BBC', 'Apple', 'Barracuda FX'];
    let accountsSample = [];
    let ccyPairsSample = []; // 'USDEUR'];

    this.data.raw.clients = clientsSample;
    this.data.raw.accounts = accountsSample;
    this.data.raw.ccyPairs = ccyPairsSample;

    this.saveFavorites();
    this.loadFavoriteModels();
  }

  /**********************************************************************/
  /* USER PROFILE METHODS */

  public getFavoritesListSize(favoriteItemType: BfxAutocompleteFavoriteTypeEnum): number {
    switch (favoriteItemType) {
      case BfxAutocompleteFavoriteTypeEnum.CLIENT:
        return this.config.favoritesListSize.clients;
      case BfxAutocompleteFavoriteTypeEnum.ACCOUNT:
        return this.config.favoritesListSize.accounts;
      case BfxAutocompleteFavoriteTypeEnum.CCY_PAIR:
        return this.config.favoritesListSize.ccyPairs;
      default:
    }
  }
  /**
   * RAW Favorites arrays should behave like a queue
   * - New items are added at the start of the favorites array
   * - Old items are removed form the end of the array
   */
  public addFavoriteItem(favoriteItemType: BfxAutocompleteFavoriteTypeEnum, itemId: string): void {
    if (
      favoriteItemType != null &&
      itemId != null &&
      typeof itemId === "string" &&
      itemId.length > 0
    ) {
      switch (favoriteItemType) {
        case BfxAutocompleteFavoriteTypeEnum.CLIENT:
          let clientIndex: number = this.data.raw.clients.indexOf(itemId);
          if (clientIndex === 0) {
            // the element that needs to be added is already in the right place, changes to raw or models (already loaded) not necessary
            return;
          }
          if (clientIndex > 0) {
            this.data.raw.clients.splice(clientIndex, 1);
          }
          while (this.data.raw.clients.length >= this.config.favoritesListSize.clients) {
            this.data.raw.clients.pop();
          }
          this.data.raw.clients.unshift(itemId);
          break;
        case BfxAutocompleteFavoriteTypeEnum.ACCOUNT:
          let accountIndex: number = this.data.raw.accounts.indexOf(itemId);
          if (accountIndex >= 0) {
            this.data.raw.accounts.splice(accountIndex, 1);
          }
          while (this.data.raw.accounts.length >= this.config.favoritesListSize.accounts) {
            this.data.raw.accounts.pop();
          }
          this.data.raw.accounts.unshift(itemId);
          break;
        case BfxAutocompleteFavoriteTypeEnum.CCY_PAIR:
          let ccyPairIndex: number = this.data.raw.ccyPairs.indexOf(itemId);
          if (ccyPairIndex >= 0) {
            this.data.raw.ccyPairs.splice(ccyPairIndex, 1);
          }
          while (this.data.raw.ccyPairs.length >= this.config.favoritesListSize.ccyPairs) {
            this.data.raw.ccyPairs.pop();
          }
          this.data.raw.ccyPairs.unshift(itemId);
          break;
        default:
          logger.out.error(
            "ERROR: BfxAutocompleteFavoritesProviderService.addFavoriteItem(): Invalid favorites type!",
          );
          break;
      }
      this.saveFavorites();
      this.loadFavoriteModels();
    }
  }

  public getFavoriteItems(
    favoriteType: BfxAutocompleteFavoriteTypeEnum,
    itemFormat: BfxAutocompleteFavoriteItemFormatEnum,
  ): Array<any> {
    if (favoriteType != null && itemFormat != null) {
      switch (favoriteType) {
        case BfxAutocompleteFavoriteTypeEnum.CLIENT:
          switch (itemFormat) {
            case BfxAutocompleteFavoriteItemFormatEnum.MODEL: // not in use
              return JSON.parse(JSON.stringify(this.data.models.clients));
            case BfxAutocompleteFavoriteItemFormatEnum.RAW:
              return JSON.parse(JSON.stringify(this.data.raw.clients));
            case BfxAutocompleteFavoriteItemFormatEnum.DISPLAY:
              return this.castToClientDisplay(
                JSON.parse(JSON.stringify(this.data.display.clients)),
              );
            default:
              return null;
          }
        case BfxAutocompleteFavoriteTypeEnum.ACCOUNT:
          switch (itemFormat) {
            case BfxAutocompleteFavoriteItemFormatEnum.MODEL: // not in use
              return JSON.parse(JSON.stringify(this.data.models.accounts));
            case BfxAutocompleteFavoriteItemFormatEnum.RAW:
              return JSON.parse(JSON.stringify(this.data.raw.accounts));
            case BfxAutocompleteFavoriteItemFormatEnum.DISPLAY:
              return this.castToAccountDisplay(
                JSON.parse(JSON.stringify(this.data.display.accounts)),
              );
            default:
              return null;
          }
        case BfxAutocompleteFavoriteTypeEnum.CCY_PAIR:
          switch (itemFormat) {
            case BfxAutocompleteFavoriteItemFormatEnum.MODEL: // not in use
              return JSON.parse(JSON.stringify(this.data.models.ccyPairs));
            case BfxAutocompleteFavoriteItemFormatEnum.RAW:
              return JSON.parse(JSON.stringify(this.data.raw.ccyPairs));
            case BfxAutocompleteFavoriteItemFormatEnum.DISPLAY:
              return this.castToCcyPairDisplay(
                JSON.parse(JSON.stringify(this.data.display.ccyPairs)),
              );
            default:
              return null;
          }
        default:
          return null;
      }
    }
  }

  private loadFavorites(): void {
    let profileConfigKey: string = ProfileSettingKeys.userProfileFavoritesAutocomplete;
    profileConfigKey = profileConfigKey.toLowerCase();

    this._profileService.getProfileConfig(profileConfigKey).then((rawValues: string) => {
      if (rawValues != null) {
        let parsedValues = JSON.parse(rawValues);
        /// CLIENTS
        if (parsedValues.clients != null && parsedValues.clients instanceof Array) {
          this.data.raw.clients = parsedValues.clients;
        }
        // ACCOUNTS
        if (parsedValues.accounts != null && parsedValues.accounts instanceof Array) {
          this.data.raw.accounts = parsedValues.accounts;
        }
        // CCY-PAIRS
        if (parsedValues.ccyPairs != null && parsedValues.ccyPairs instanceof Array) {
          this.data.raw.ccyPairs = parsedValues.ccyPairs;
        }
        this.loadFavoriteModels();
      } else {
        logger.out.error("ERROR: Profile returned null!");
      }
    });
  }

  private saveFavorites(): void {
    this.saveSubject.next();
  }
  /**********************************************************************/
  /* Model Loading */

  private loadFavoriteModels(): void {
    if (!this.control.modelsLoading) {
      this.ctrlModelsLoadingLock();
      /* CLIENT Models */
      this.data.models.clients = new Array<CubiqTS.ClientImpl>();
      this.data.display.clients = new Array<BfxAutocompleteClientModel>();
      this.data.raw.clients.slice(0).forEach((item) => {
        this.clientLookup(item)
          .then((result: BfxAutocompleteClientModel | null) => {
            if (result == null) {
              let removeIndex = this.data.raw.clients.indexOf(item);
              this.data.raw.clients.splice(removeIndex, 1);
              this.ctrlModelsLoadingUnlock();
            } else {
              let duplicateClientIndex = this.data.display.clients.findIndex((clientModel) => {
                return clientModel.value === item;
              });
              if (duplicateClientIndex < 0) {
                this.data.display.clients.push(result);
                this.ctrlModelsLoadingUnlock();
              }
            }
          })
          .catch((ex) => {
            logger.out.warn("WARNING: ", ex, " - Error handling started!");
            this.handleFavoritesEntryError(BfxAutocompleteFavoriteTypeEnum.CLIENT, item);
            this.ctrlModelsLoadingUnlock();
          });
      });
      /* ACCOUNT Models */
      this.data.models.accounts = new Array<CubiqTS.AccountImpl>();
      this.data.display.accounts = new Array<BfxAutocompleteAccountModel>();
      this.data.raw.accounts.slice(0).forEach((item) => {
        this.accountLookup(item)
          .then((result: BfxAutocompleteAccountModel | null) => {
            if (result == null) {
              let removeIndex = this.data.raw.clients.indexOf(item);
              this.data.raw.clients.splice(removeIndex, 1);
              this.ctrlModelsLoadingUnlock();
            } else {
              let duplicateAccountsIndex = this.data.display.accounts.findIndex((accountModel) => {
                return accountModel.value === item;
              });
              if (duplicateAccountsIndex < 0) {
                this.data.display.accounts.push(result);
                this.ctrlModelsLoadingUnlock();
              }
            }
          })
          .catch((ex) => {
            logger.out.warn("WARNING: ", ex);
            this.handleFavoritesEntryError(BfxAutocompleteFavoriteTypeEnum.ACCOUNT, item);
            this.ctrlModelsLoadingUnlock();
          });
      });
      /* CCY_PAIRS Models */
      this.data.display.ccyPairs = new Array<BfxAutocompleteGenericModel>();
      this.data.raw.ccyPairs.slice(0).forEach((item) => {
        this.ccyPairValidate(item).then((result) => {
          if (result != null) {
            this.data.display.ccyPairs.push(new BfxAutocompleteGenericModel(item, item));
            this.ctrlModelsLoadingUnlock();
          } else {
            logger.out.warn("WARNING: invalid ccy pair [" + item + "]");
            this.handleFavoritesEntryError(BfxAutocompleteFavoriteTypeEnum.CCY_PAIR, item);
            this.ctrlModelsLoadingUnlock();
          }
        });
      });
    }
  }
  /* Locking mechanism for loadFavoriteModels() to avoid concurrent loading */
  private ctrlModelsLoadingLock(): void {
    this.control.modelsLoading = true;
    setTimeout(() => {
      // fall back - unlock after 10 secs
      if (this.control.modelsLoading) {
        logger.out.info("INFO: manual unlock of the favorite model loading procedure in effect!");
        this.control.modelsLoading = false;
        this.control.modelsLoadingPromiseCount = 0;
      }
    }, 10000);
  }

  private ctrlModelsLoadingUnlock(): void {
    this.control.modelsLoadingPromiseCount++;
    let loadingSize =
      this.data.raw.clients.length + this.data.raw.accounts.length + this.data.raw.ccyPairs.length;
    if (this.control.modelsLoadingPromiseCount >= loadingSize) {
      this.control.modelsLoading = false;
      this.control.modelsLoadingPromiseCount = 0;
    }
  }
  /**********************************************************************/
  /* Lookups & Validators */

  private clientLookup(clientId: string): Promise<BfxAutocompleteClientModel> {
    return new Promise((res, rej) => {
      this._clientLookupService.getActiveClient(clientId).then((result: CubiqTS.ClientImpl) => {
        if (result != null && result instanceof CubiqTS.ClientImpl) {
          if (!ClientAccountVisibilityType.isFull(result)) {
            // lookup worked, but result is unusable for entry
            res(null);
          } else {
            res(
              new BfxAutocompleteClientModel(
                result.clientId,
                result.clientId,
                result.clientId,
                result.clientName,
                StaticDataPrivatizationType.hasPrivatization(result),
              ),
            );
          }
        } else {
          rej("Client lookup result is null or wrong type [required-type: ClientImpl]!");
        }
      });
    });
  }

  private accountLookup(accountId: string): Promise<BfxAutocompleteAccountModel> {
    return new Promise((res, rej) => {
      this._accountLookupService.getActiveAccount(accountId).then((result: CubiqTS.AccountImpl) => {
        if (result != null && result instanceof CubiqTS.AccountImpl) {
          if (!ClientAccountVisibilityType.isFull(result)) {
            // lookup worked, but result is unusable for entry
            res(null);
          } else {
            res(
              new BfxAutocompleteAccountModel(
                result.id,
                result.id,
                result.id,
                result.name,
                result.bookingCode,
                result.contact,
                StaticDataPrivatizationType.hasPrivatization(result),
              ),
            );
          }
        } else {
          rej("Account lookup result is null or wrong type [required-type: AccountImpl]!");
        }
      });
    });
  }

  private ccyPairValidate(ccyPairCode: string): Promise<boolean> {
    return new Promise((res, rej) => {
      let currencyPairLookup: CcyPairLookupService = this._ccyPairLookupService.getBase();
      let currencyPairModel = currencyPairLookup.getItem(ccyPairCode);

      if (currencyPairModel != null) {
        res(true);
      } else {
        currencyPairModel = currencyPairLookup.getCcyPair(
          ccyPairCode.substr(3, 3),
          ccyPairCode.substr(0, 3),
        );
        if (currencyPairModel != null && currencyPairModel.allowInvertsFg) {
          res(true);
        } else {
          res(false);
        }
      }
    });
  }
  /**********************************************************************/
  /* Error handling */

  private handleFavoritesEntryError(
    favoriteType: BfxAutocompleteFavoriteTypeEnum,
    itemId: string,
  ): void {
    let index;

    if (itemId != null && itemId.length > 0) {
      switch (favoriteType) {
        case BfxAutocompleteFavoriteTypeEnum.CLIENT:
          index = this.data.raw.clients.indexOf(itemId);
          if (index >= 0 && this.data.raw.clients.length > index) {
            this.data.raw.clients.splice(index, 1);
            this.saveFavorites();
            logger.out.warn(
              'SUCCESS: Invalid item "' + itemId + '" successfully removed from profile!',
            );
          }
          break;
        case BfxAutocompleteFavoriteTypeEnum.ACCOUNT:
          index = this.data.raw.accounts.indexOf(itemId);
          if (index >= 0 && this.data.raw.accounts.length > index) {
            this.data.raw.accounts.splice(index, 1);
            this.saveFavorites();
            logger.out.warn(
              'SUCCESS: Invalid item "' + itemId + '" successfully removed from profile!',
            );
          }
          break;
        case BfxAutocompleteFavoriteTypeEnum.CCY_PAIR:
          index = this.data.raw.ccyPairs.indexOf(itemId);
          if (index >= 0 && this.data.raw.ccyPairs.length > index) {
            this.data.raw.ccyPairs.splice(index, 1);
            this.saveFavorites();
            logger.out.warn(
              'SUCCESS: Invalid item "' + itemId + '" successfully removed from profile!',
            );
          }
          break;
        default:
          logger.out.error("ERROR: Undetermined item type");
          break;
      }
    } else {
      logger.out.error("ERROR: Error handling procedure FAIL - Unknown itemId!");
    }
  }

  /**********************************************************************/
  /* Error handling */
  private castToAccountDisplay(accounts: Array<any>): Array<BfxAutocompleteAccountModel> {
    let result: Array<BfxAutocompleteAccountModel> = new Array<BfxAutocompleteAccountModel>();
    accounts.forEach((item) => {
      result.push(
        new BfxAutocompleteAccountModel(
          item.value,
          item.label,
          item.id,
          item.name,
          item.booking,
          item.contact,
        ),
      );
    });
    return result;
  }

  private castToClientDisplay(clients: Array<any>): Array<BfxAutocompleteClientModel> {
    let result: Array<BfxAutocompleteClientModel> = new Array<BfxAutocompleteClientModel>();
    clients.forEach((item) => {
      result.push(new BfxAutocompleteClientModel(item.value, item.label, item.id, item.name));
    });
    return result;
  }

  private castToCcyPairDisplay(ccyPairs: Array<any>): Array<BfxAutocompleteGenericModel> {
    let result: Array<BfxAutocompleteGenericModel> = new Array<BfxAutocompleteGenericModel>();
    ccyPairs.forEach((item) => {
      result.push(new BfxAutocompleteGenericModel(item.value, item.label));
    });
    return result;
  }
}
